import { Sort } from "../_types/sort";
import { AdsPage } from "../enums/ad-page";

const kinds = [
	{
		name: "فیلم",
		englishName: "Movies",
		kind: "movies",
		type: "movie",
		typeEnum: "MOVIE",
	},
	{
		name: "سریال",
		englishName: "Series",
		kind: "series",
		type: "series",
		typeEnum: "SERIES",
	},
	{
		name: "بازی",
		englishName: "Games",
		kind: "games",
		type: "game",
		typeEnum: "GAME",
	},
	{
		name: "بازی رومیزی",
		englishName: "Board games",
		kind: "board-games",
		type: "board_game",
		typeEnum: "BOARD_GAME",
	},
];

export const getKinds = () => {
	return kinds;
};

const contentSorts: Sort[] = [
	{
		name: "بیشترین امتیاز",
		value: "cutinScore",
		sortBy: "cutinScore",
		direction: "desc",
	},
	{
		name: "کمترین امتیاز",
		value: "cutinScore",
		sortBy: "cutinScore",
		direction: "asc",
	},
	{
		name: "جدیدترین ها",
		value: "newest",
		sortBy: "createdDate",
		direction: "desc",
	},
	{
		name: "قدیمی‌ترین ها",
		value: "newest",
		sortBy: "createdDate",
		direction: "asc",
	},
	{
		name: "بیشترین تعداد رای",
		value: "totalScore",
		sortBy: "totalScore",
		direction: "desc",
	},
];

export function getObjectByKind(kindStr: string) {
	return kinds.find((kind) => kind.kind === kindStr) || null;
}

export function getType(type: string) {
	return kinds.find((kind) => kind.type === type) || null;
}

export function decodeToReadableCategory(enc: string) {
	const decoded = decodeURIComponent(enc);
	return decoded;
}

export function getLabelFromEnumOptions<E extends string>(
	value: E,
	options: ReadonlyArray<{ value: E; label: string }>,
): string {
	const found = options.find((o) => o.value === value);
	return found?.label ?? "";
}

export function minutesToPersian(mins: number): string {
	if (!Number.isFinite(mins) || mins < 0) {
		throw new Error("mins must be a non-negative finite number");
	}

	const hours = Math.floor(mins / 60);
	const minutes = mins % 60;

	const parts: string[] = [];

	if (hours > 0) {
		parts.push(`${hours} ساعت`);
	}

	if (minutes > 0) {
		parts.push(`${minutes} دقیقه`);
	}

	if (parts.length === 0) {
		return "0 دقیقه";
	}

	return parts.join(" و ");
}

export function getSortByKind(kind?: string): Sort[] {
	if (kind === "movies" || kind === "series") {
		return getMovieAndSeriesSorts();
	} else if (kind === "games") {
		return getGameSorts();
	} else if (kind === "board-games") {
		return getBoardGameSorts();
	} else {
		return [...contentSorts];
	}
}

export function getMovieAndSeriesSorts(): Sort[] {
	return [
		...contentSorts,
		[
			{
				name: "امتیاز imdb",
				value: "imdb.score",
				sortBy: "imdb.score",
				direction: "desc",
			},
			{
				name: "امتیاز metacritics",
				value: "metacritic.score",
				sortBy: "metacritic.score",
				direction: "desc",
			},
			{
				name: "امتیاز Rotten Tomatoes",
				value: "rottenTomatoes.score",
				sortBy: "rottenTomatoes.score",
				direction: "desc",
			},
		],
	].flat();
}

export function getGameSorts(): Sort[] {
	return [
		...contentSorts,
		[
			{
				name: "امتیاز ign",
				value: "ignModel.score",
				sortBy: "ignModel.score",
				direction: "desc",
			},
			{
				name: "امتیاز metacritics",
				value: "metacritic.score",
				sortBy: "metacritic.score",
				direction: "desc",
			},
			{
				name: "امتیاز Game Spot",
				value: "gameSpotModel.score",
				sortBy: "gameSpotModel.score",
				direction: "desc",
			},
		],
	].flat();
}

export function getBoardGameSorts(): Sort[] {
	return [
		...contentSorts,
		[
			{
				name: "امتیاز Board Game Geek",
				value: "boardGameGeek.score",
				sortBy: "boardGameGeek.score",
				direction: "desc",
			},
		],
	].flat();
}

export function getNewsSorts(): Sort[] {
	return [
		{
			name: "جدیدترین ها",
			value: "newest",
			sortBy: "createdDate",
			direction: "desc",
		},
		{
			name: "قدیمی‌ترین ها",
			value: "newest",
			sortBy: "createdDate",
			direction: "asc",
		},
	].flat();
}

export const getGenresByKind = async (kind: any) => {
	const url = new URL(
		`${process.env.NEXT_PUBLIC_API_URL}/public/${kind.kind}-genres`,
	);
	const res = await fetch(url.toString(), {
		method: "GET",
		headers: {
			"Content-Type": "application/json",
		},
		cache: "no-store",
	});

	if (!res.ok) throw new Error("Failed to fetch genres");
	return res.json();
};

export const getAdsByPage = async (adsPage: AdsPage) => {
	const url = new URL(`${process.env.NEXT_PUBLIC_API_URL}/public/ads/search`);

	url.searchParams.append("adsPage", adsPage);

	const res = await fetch(url.toString(), {
		method: "GET",
		headers: {
			"Content-Type": "application/json",
		},
		cache: "no-store",
	});

	if (!res.ok) throw new Error("Failed to fetch pages");
	return res.json();
};

export function timeAgo(createdDate: string): string {
	const now = new Date();
	const created = new Date(createdDate);
	const diffMs = now.getTime() - created.getTime();

	const diffMinutes = Math.floor(diffMs / (1000 * 60));
	const diffHours = Math.floor(diffMinutes / 60);

	if (diffMinutes < 1) {
		return "لحظاتی پیش";
	}

	if (diffMinutes < 60) {
		return `${diffMinutes} دقیقه پیش`;
	}

	if (diffHours < 24) {
		const minutesRemainder = diffMinutes % 60;
		if (minutesRemainder === 0) {
			return `${diffHours} ساعت پیش`;
		}
		return `${diffHours} ساعت و ${minutesRemainder} دقیقه پیش`;
	}

	return created.toLocaleString("fa-IR");
}

export function createTrailer(item: any, kind: any, trailer?: string) {
	if (!trailer) {
		return null;
	}

	return {
		videoUrl: trailer,
		id: item.id,
		title: item.title,
		cutinScore: item.cutinScore,
		bannerImage: item.backgroundImage,
		totalScore: item.totalScore,
		content: {
			_class: kind.type,
		},
	};
}

export function limitDecimalsToString(num: number, toLimit: number) {
	if (!num) {
		return "0";
	}
	return num.toLocaleString(undefined, {
		maximumFractionDigits: toLimit,
	});
}

export const trackClick = async (route: string) => {
	try {
		await fetch(
			`${process.env.NEXT_PUBLIC_API_URL}/public/track/click/page${route}`,
			{
				method: "GET",
				headers: { "Content-Type": "application/json" },
				keepalive: true,
			},
		);
	} catch (err) {
		console.warn("Click tracking failed:", err);
	}
};

export const trackClickAds = async (adId: string, pageType: string) => {
	try {
		await fetch(
			`${process.env.NEXT_PUBLIC_API_URL}/public/track/click/ad/${adId}?ref=${pageType}`,
			{
				method: "GET",
				headers: { "Content-Type": "application/json" },
				keepalive: true,
			},
		);
	} catch (err) {
		console.warn("Click tracking failed:", err);
	}
};
